import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { Link, matchPath, Navigate, useLocation } from "react-router-dom";
import { getApi } from "../../services/api/globalApi";

export function createAccess(session) {
  const isAdmin = session?.role === "admin";
  const permissions = session?.permissions || { sidebar: [], dashboardReports: [] };
  const menu = session?.menu || [];
  const allowedItems = menu.flatMap(group => group.items).filter(item => permissions.sidebar.includes(item.id));
  const canVisit = path => isAdmin || allowedItems.some(item => item.routes.some(route => matchPath({ path: route, end: true }, path.split("?")[0])));
  return {
    role: session?.role, isAdmin, permissions, menu, reportTypes: session?.reportTypes || [], canVisit,
    canReport: id => isAdmin || (canVisit("/") && permissions.dashboardReports.includes(id)),
    canMenuGroup: id => isAdmin || menu.some(group => group.id === id && group.items.some(item => permissions.sidebar.includes(item.id))),
    landingPath: allowedItems[0]?.id || null,
  };
}

export const AdminAccessContext = createContext(createAccess(null));
export const useAdminAccess = () => useContext(AdminAccessContext);

export function AdminAccessProvider({ children }) {
  const [session, setSession] = useState(null);
  const [error, setError] = useState("");
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const response = await getApi({ path: "admin/settings/access/me" });
        const value = response?.data?.result;
        if (!["admin", "warehouseKeeper"].includes(value?.role) || !Array.isArray(value?.menu)) throw new Error("invalid access response");
        if (active) { setSession(value); setError(""); }
      } catch (_) {
        if (active) setError("دریافت سطح دسترسی انجام نشد. دوباره تلاش کنید.");
      }
    };
    load();
    const timer = window.setInterval(load, 30000);
    window.addEventListener("focus", load);
    return () => { active = false; window.clearInterval(timer); window.removeEventListener("focus", load); };
  }, [attempt]);
  const reload = useCallback(() => { setError(""); setAttempt(value => value + 1); }, []);
  if (!session) return <div className="m-8 rounded-2xl bg-white p-6" dir="rtl">
    {error ? <p role="alert">{error} <button type="button" className="m-3 text-blue-700" onClick={reload}>تلاش دوباره</button><Link to="/login" className="text-blue-700">ورود دوباره</Link></p> : <p role="status">در حال دریافت سطح دسترسی…</p>}
  </div>;
  return <AdminAccessContext.Provider value={createAccess(session)}>{children}</AdminAccessContext.Provider>;
}

export function AccessRoute({ children }) {
  const { pathname } = useLocation();
  const access = useAdminAccess();
  if (access.canVisit(pathname)) return children;
  if (pathname === "/" && access.landingPath) return <Navigate to={access.landingPath} replace />;
  return <div role="alert" className="rounded-2xl border bg-white p-8 text-slate-700">
    <p>شما به این بخش دسترسی ندارید.</p>
    {access.landingPath ? <Link className="mt-4 inline-block text-blue-700" to={access.landingPath}>رفتن به بخش مجاز</Link> : <p className="mt-3">هنوز دسترسی‌ای برای نقش انباردار فعال نشده است. با مدیر تماس بگیرید.</p>}
  </div>;
}
